import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Fade,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  Remove,
  Add,
  Star,
  StarOutline,
  Quiz,
  Search,
  Visibility,
  FolderOpen,
  Chat,
  Shield,
} from '@mui/icons-material';
import { useOnboardingChecklist } from '../../contexts/OnboardingChecklistContext';
import StarCelebration from './StarCelebration';

// Tasks that require authentication (portfolio + its risk analysis need Firestore)
const AUTH_REQUIRED_TASKS: ReadonlySet<string> = new Set(['firstRiskAnalysis', 'firstPortfolio']);

// Icon mapping for task types
const TASK_ICONS: Record<string, React.ElementType> = {
  quiz: Quiz,
  search: Search,
  visibility: Visibility,
  folder: FolderOpen,
  chat: Chat,
  shield: Shield,
};

const OnboardingChecklist: React.FC = () => {
  const theme = useTheme();
  const {
    taskMetas,
    starsEarned,
    totalStars,
    isVisible,
    isCollapsed,
    isGuest,
    allComplete,
    showStarCelebration,
    celebrationTaskTitle,
    dismissCelebration,
    dismissChecklist,
    toggleCollapse,
  } = useOnboardingChecklist();

  // Render star celebration overlay
  const celebrationOverlay = (
    <StarCelebration
      visible={showStarCelebration}
      taskTitle={celebrationTaskTitle}
      starsEarned={starsEarned}
      totalStars={totalStars}
      onDismiss={dismissCelebration}
    />
  );

  if (!isVisible) return celebrationOverlay;

  // Celebration state — all stars earned
  if (allComplete) {
    return (
      <>
        {celebrationOverlay}
        <Fade in>
          <Paper
            className="no-print"
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 1200,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              minWidth: 280,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                {Array.from({ length: totalStars }).map((_, i) => (
                  <Star key={i} sx={{ fontSize: 18, color: theme.palette.success.main }} />
                ))}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                You're all set!
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </>
    );
  }

  // Collapsed pill
  if (isCollapsed) {
    return (
      <>
        {celebrationOverlay}
        <Fade in>
          <Paper
            elevation={8}
            onClick={toggleCollapse}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 1200,
              px: 2,
              py: 1,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': { borderColor: 'text.primary' },
            }}
          >
            <Add sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box sx={{ display: 'flex', gap: 0.25, mr: 0.5 }}>
              {Array.from({ length: totalStars }).map((_, i) => (
                i < starsEarned ? (
                  <Star key={i} sx={{ fontSize: 14, color: theme.palette.success.main }} />
                ) : (
                  <StarOutline key={i} sx={{ fontSize: 14, color: 'text.disabled' }} />
                )
              ))}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {starsEarned}/{totalStars}
            </Typography>
          </Paper>
        </Fade>
      </>
    );
  }

  // Expanded panel
  return (
    <>
      {celebrationOverlay}
      <Fade in>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 1200,
            width: 320,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Header with stars */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Get Started
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                {Array.from({ length: totalStars }).map((_, i) => (
                  i < starsEarned ? (
                    <Star key={i} sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  ) : (
                    <StarOutline key={i} sx={{ fontSize: 16, color: 'text.disabled' }} />
                  )
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={toggleCollapse} sx={{ color: 'text.secondary' }}>
                <Remove sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={dismissChecklist} sx={{ color: 'text.secondary' }}>
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Tasks */}
          <Box sx={{ p: 1.5 }}>
            {taskMetas.map((task) => {
              const TaskIcon = TASK_ICONS[task.icon] || Quiz;
              return (
                <Box
                  key={task.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: task.completed
                      ? (theme) => alpha(theme.palette.success.main, 0.04)
                      : 'transparent',
                  }}
                >
                  {/* Star or outline */}
                  <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                    {task.completed ? (
                      <Star sx={{ fontSize: 20, color: theme.palette.success.main }} />
                    ) : (
                      <StarOutline sx={{ fontSize: 20, color: 'text.disabled' }} />
                    )}
                  </Box>

                  {/* Task icon */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <TaskIcon
                      sx={{
                        fontSize: 16,
                        color: task.completed ? 'text.secondary' : 'text.primary',
                      }}
                    />
                  </Box>

                  {/* Text */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: task.completed ? 'text.secondary'
                          // Secondary, not disabled: a locked step is still read ("Sign in to
                          // unlock"), and text.disabled measured 2.68:1 on white ([[BUGS #430]]).
                          : (isGuest && AUTH_REQUIRED_TASKS.has(task.id)) ? 'text.secondary'
                          : 'text.primary',
                        textDecoration: task.completed ? 'line-through' : 'none',
                      }}
                    >
                      {task.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {isGuest && AUTH_REQUIRED_TASKS.has(task.id) && !task.completed
                        ? 'Sign in to unlock'
                        : task.subtitle}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default OnboardingChecklist;
